import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import { useApi } from "@/hooks/useApi";
import type { BizPost } from "@/lib/types";
import { SkeletonCard } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import { useAuth } from "@/components/feature/AuthProvider";
import PromoCard from "./components/PromoCard";

export default function PromoPage() {
  const navigate = useNavigate();
  const { member, requireLogin } = useAuth();
  const { data, loading, error, reload } = useApi<BizPost[]>("bizposts.php?size=20");

  const posts = useMemo(() => {
    const list = data ?? [];
    return [...list].sort((a, b) => (b.is_top ?? 0) - (a.is_top ?? 0));
  }, [data]);

  const handleWrite = () => {
    if (!requireLogin()) return;
    navigate("/promo/write");
  };

  return (
    <>
      <PageBanner
        title="직업홍보"
        description="회원 사업장의 소식과 홍보 글을 만나보세요."
        breadcrumb={[{ label: "직업홍보" }]}
      />

      <section className="mx-auto max-w-6xl px-4 md:px-6 py-10 md:py-12">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="홍보 글을 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        ) : posts.length === 0 ? (
          <EmptyState
            icon="ri-megaphone-line"
            title="등록된 홍보 글이 없습니다"
            description="첫 번째 홍보 글을 작성해 보세요."
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {posts.map((post) => (
              <PromoCard key={post.no} post={post} />
            ))}
          </div>
        )}

        {!member && (
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-3 rounded-xl border border-background-200 bg-background-100 p-5">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-lock-line text-lg" aria-hidden="true"></i>
              </span>
              <div>
                <p className="font-semibold text-foreground-800">직업홍보 글쓰기</p>
                <p className="text-sm text-foreground-500">로그인 후 작성 가능합니다.</p>
              </div>
            </div>
            <Link
              to="/free"
              className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
            >
              자유글 보러가기
            </Link>
          </div>
        )}
      </section>

      {/* 플로팅 글쓰기 버튼 */}
      <button
        type="button"
        onClick={handleWrite}
        className="fixed bottom-24 xl:bottom-8 right-4 md:right-6 z-[140] inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-secondary-500 px-5 py-3.5 text-sm font-bold text-primary-950 shadow-lg hover:bg-secondary-600 transition-colors"
      >
        <i className="ri-add-line text-lg" aria-hidden="true"></i>
        홍보글 쓰기
      </button>
    </>
  );
}